import { useCallback, useRef } from 'react';
import { Article, PaginatedResponse } from '@repo/types';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperType } from 'swiper';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import { ArticleCard, ArticleCardSkeleton } from '../ArticleCard';
import { ArticleCarouselProps } from './contracts';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

export type ArticleCarouselUIProps = ArticleCarouselProps & {
  paginatedArticles: PaginatedResponse<Article>;
  allArticles?: Article[];
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
  lazyLoad?: boolean;
};

export default function ArticleCarouselUI({
  title,
  paginatedArticles,
  allArticles,
  hasNextPage = false,
  isFetchingNextPage = false,
  fetchNextPage,
  lazyLoad = false,
  variant,
  showDate,
  showAuthor,
  showImage,
  showSummary,
  showTag,
  autoplay = false,
  autoplayDelay = 3000,
  loop = false,
  prioritize = false,
}: ArticleCarouselUIProps) {
  const swiperRef = useRef<SwiperType | null>(null);

  const articles = lazyLoad && allArticles ? allArticles : paginatedArticles?.items ?? [];

  const handleSlideChange = useCallback(
    (swiper: SwiperType) => {
      if (!lazyLoad || !fetchNextPage || !hasNextPage || isFetchingNextPage) {
        return;
      }

      const slidesPerView =
        typeof swiper.params.slidesPerView === 'number' ? swiper.params.slidesPerView : swiper.slidesPerViewDynamic();
      const currentIndex = swiper.activeIndex;
      const totalSlides = swiper.slides.length;

      // Fetch more when we're within 2 slides of the end
      const threshold = 2;
      if (currentIndex + slidesPerView + threshold >= totalSlides) {
        fetchNextPage();
      }
    },
    [lazyLoad, fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  if (articles.length === 0 && !isFetchingNextPage) {
    return null;
  }

  const autoplayConfig = autoplay
    ? {
        delay: autoplayDelay,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      }
    : false;

  const skeletonCount = 3;

  return (
    <div className="article-carousel">
      {title && <h3 className="article-carousel__title">{title}</h3>}
      <Swiper
        key={`swiper-autoplay-${autoplay}`}
        modules={[Navigation, Pagination, Autoplay]}
        slidesPerView="auto"
        spaceBetween={16}
        navigation
        pagination={{ clickable: true }}
        autoplay={autoplayConfig}
        loop={lazyLoad ? false : loop}
        className="article-carousel__swiper"
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        onSlideChange={handleSlideChange}
        onReachEnd={() => {
          if (lazyLoad && fetchNextPage && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        }}
      >
        {articles.map((article) => (
          <SwiperSlide key={article.id} className="article-carousel__slide">
            <ArticleCard
              variant={variant}
              article={article}
              showDate={showDate}
              showAuthor={showAuthor}
              showImage={showImage}
              showSummary={showSummary}
              showTag={showTag}
              prioritize={prioritize}
            />
          </SwiperSlide>
        ))}
        {lazyLoad &&
          isFetchingNextPage &&
          Array.from({ length: skeletonCount }).map((_, index) => (
            <SwiperSlide key={`skeleton-${index}`} className="article-carousel__slide article-carousel__slide--skeleton">
              <ArticleCardSkeleton
                variant={variant}
                showImage={showImage}
                showTag={showTag}
                showDate={showDate}
                showAuthor={showAuthor}
                showSummary={showSummary}
              />
            </SwiperSlide>
          ))}
      </Swiper>
    </div>
  );
}
